import { useSelector } from 'react-redux';

// components
import { AccordionItem, Container, Col, Button } from '@geberit/gdds';

// styles
import styles from './anchorbar.module.scss';

// types
import type {
  ContentElementPayload,
  ShowroomLocatorTeaserPayload,
} from 'components/ContentElementsGdds/content-elements-payload';

// utils
import { buildSize, gridSizes } from 'utils/gridSize';
import { scrollYToElement } from 'utils/scrollTo';
import { getSectionId } from 'utils/get-section-id';
import { isEmpty } from 'utils/is-empty';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { anchorBarSelector } from 'utils/selectors/globalsSelectors';

type AnchorBarProps = {
  content: Exclude<ContentElementPayload, ShowroomLocatorTeaserPayload>[];
  collapseAnchor?: boolean;
  pageAnchorTitle?: string;
  className?: string;
};

export function AnchorBar({
  content,
  pageAnchorTitle,
  className,
  collapseAnchor,
}: Readonly<AnchorBarProps>) {
  const translate = useTranslationFunction();
  const anchorBarData = useSelector(anchorBarSelector);
  const title = anchorBarData?.title || translate('web20_content');

  // Note: Reducing the content object (for the use inside the AnchorBar)
  // take only the results that have " titleInAnchor === 'true' "
  // Note: Usage of toString() because some data provide strings instead of booleans
  const contentInAnchorBar = content.filter((item) => item.titleInAnchor?.toString() === 'true');

  // we need only specific properties
  const anchorBarAllowedProperties = ['anchor', 'title'];

  // map the array of objects with the needed properties
  const anchorBarContent = contentInAnchorBar.map((item) => {
    const allKeys = Object.keys(item);

    return allKeys.reduce((next, key) => {
      if (anchorBarAllowedProperties.includes(key)) {
        return { ...next, [key]: item[key] };
      } else {
        return next;
      }
    }, {});
  });

  if (isEmpty(anchorBarContent)) return null;

  const getLink = (item): { id: string | undefined; title: string | undefined } => {
    return {
      id: getSectionId((item.anchor || item.title) ?? '', undefined, item.contentIndex ?? 0),
      title: item.anchor || item.title,
    };
  };

  const anchorLinks = anchorBarContent.map(getLink);

  const onClick = (e, id) => {
    e.preventDefault();

    const element = document.getElementById(id);
    if (element) {
      scrollYToElement(element);
    }
  };

  return (
    <div className={classNameBuilder(className, styles.anchorbarWrapper)}>
      <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)}>
        <Col size={[4, 8, 12]} className={styles.anchorbarCol}>
          <div>
            <AccordionItem
              iconAlignment="right"
              title={pageAnchorTitle || title}
              fontSize="p1"
              opened={!collapseAnchor}
            >
              <div className={styles.anchorbarLinkWrapper}>
                {anchorLinks.map((item) => (
                  <Button
                    key={item.id}
                    className={styles.anchorbarItem}
                    onClick={(e) => onClick(e, item.id)}
                    stylingType="flat"
                    stretched
                    alignByContent="left"
                  >
                    <span>{item.title}</span>
                  </Button>
                ))}
              </div>
            </AccordionItem>
          </div>
        </Col>
      </Container>
    </div>
  );
}
